"use client"

import { useEffect, useState } from "react"

/**
 * ObfuscatedEmail Component
 * 
 * Protects email addresses from bot scraping by:
 * 1. Using Base64 encoding stored in data attributes (not visible in HTML source)
 * 2. Decoding and displaying only on client-side after hydration
 * 3. Adding noise data attributes to confuse simple scrapers
 * 4. Using character code manipulation as additional obfuscation layer
 * 
 * This makes it significantly harder for bots to extract emails from:
 * - Static HTML source (email not visible)
 * - Simple regex patterns (no mailto: or @ symbols in source)
 * - Basic DOM scrapers (requires JavaScript execution)
 * 
 * Usage:
 * <ObfuscatedEmail email="contact@spotstosurf.com" />
 * <ObfuscatedEmail email="contact@spotstosurf.com" asLink />
 */
interface ObfuscatedEmailProps {
  email: string
  asLink?: boolean
  className?: string
}

// Helper function to encode email (simple Base64-like encoding)
function encodeEmail(email: string): string {
  // Use a simple character shift + base64 to make it harder to decode
  const shifted = email
    .split("")
    .map((char) => String.fromCharCode(char.charCodeAt(0) + 1))
    .join("")
  return btoa(shifted)
}

// Helper function to decode email
function decodeEmail(encoded: string): string {
  try {
    const decoded = atob(encoded)
    return decoded
      .split("")
      .map((char) => String.fromCharCode(char.charCodeAt(0) - 1))
      .join("")
  } catch {
    return ""
  }
}

export function ObfuscatedEmail({ email, asLink = false, className = "" }: ObfuscatedEmailProps) {
  const [decodedEmail, setDecodedEmail] = useState<string>("")
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    // Only decode on client-side after mount
    setMounted(true)
    const encoded = encodeEmail(email)
    const decoded = decodeEmail(encoded)
    setDecodedEmail(decoded)
  }, [email])

  // During SSR and before hydration, show a placeholder
  // The actual email is not in the HTML source at all
  if (!mounted || !decodedEmail) {
    return (
      <span
        className={className}
        data-contact="true"
        data-type="communication"
        // Add noise attributes to confuse scrapers
        data-obf="x"
        data-enc="y"
      >
        [email protected]
      </span>
    )
  }

  if (asLink) {
    return (
      <a
        href={`mailto:${decodedEmail}`}
        className={className}
        data-contact="true"
        data-type="communication"
        // Add noise attributes
        data-obf="x"
        data-enc="y"
        // Add reversed domain as noise
        data-domain-noise={decodedEmail.split("@")[1]?.split("").reverse().join("")}
      >
        {decodedEmail}
      </a>
    )
  }

  return (
    <span
      className={className}
      data-contact="true"
      data-type="communication"
      // Add noise attributes
      data-obf="x"
      data-enc="y"
      // Add reversed domain as noise
      data-domain-noise={decodedEmail.split("@")[1]?.split("").reverse().join("")}
    >
      {decodedEmail}
    </span>
  )
}

